import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"
import { OFFERS_QUERY_KEYS } from "@/api/hooks/offers"
import type { VerticalOption } from "@/lib/verticalCatalog"

export interface KlowArticle {
  id: number
  created_by: number
  created_by_username?: string | null
  external_article_id: number | null
  rsoc_request_id: number | null
  bulk_id: string | null
  domain: string
  slug: string | null
  base_url?: string | null
  category_id: number | null
  status: string | null
  title: string | null
  terms: string | null
  language: string | null
  url: string | null
  content: string | null
  thumbnail: string | null
  keywords: string | null
  ad_copy_text: string | null
  traffic_source: string | null
  pixel_id: string | null
  user_tracking: string | null
  user_tracking_2: string | null
  publisher_tracking_1: string | null
  publisher_tracking_2: string | null
  publisher_tracking_3: string | null
  rsoc_template_id: string | null
  google_template_id: string | null
  generation_status: string | null
  generation_error: string | null
  suggested_terms: unknown
  suggested_ad_headlines: unknown
  final_url: string | null
  last_synced_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface KlowCategoryItem {
  id?: number
  category_id?: number
  id_category?: number
  name?: string
  category?: string
  category_name?: string
}

export interface KlowCategory {
  domain: string
  categories: KlowCategoryItem[]
}

export interface RsocTemplate {
  ct: string
  NAME: string
  image?: string
}

export interface GoogleTemplate {
  style_id: string
  image_lander?: string
  image_serp?: string
}

export interface KlowArticlesListResponse {
  success: boolean
  data: KlowArticle[]
  synced_count?: number
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

export interface KlowGenerateResponse {
  success: boolean
  data: {
    article?: KlowArticle
    job_id?: number
    request_id?: number
    bulk_id?: string
  }
}

export interface KlowGenerationStatusResponse {
  success: boolean
  data: Record<string, unknown>
  article?: KlowArticle
  articles?: KlowArticle[]
  job_id?: number
}

export interface KlowOfferCreateResponse {
  success: boolean
  message?: string
  data: {
    id: number
    public_slug: string
    name: string
    network: string
    target_url: string | null
    status: string
    klow_article_id: number | null
  }
}

export interface KlowBulkGenerateItem {
  topic: string
  title?: string
  keywords?: string
  language?: string
  additional?: string
}

export interface KlowPixel {
  id: number
  rsoc_spid: number
  name: string
  label: string
  type: string
  default?: number
  gtm?: string | null
  user_ids: number[]
  owner_usernames: string[]
  owner_username?: string | null
  s2s_pixel_code?: string | null
  s2s_access_token?: string | null
  s2s_events?: Record<string, string> | null
  created_at?: string | null
  updated_at?: string | null
}

export interface KlowPixelsResponse {
  success: boolean
  data: KlowPixel[]
}

export interface KlowPixelDetailsResponse {
  success: boolean
  data: KlowPixel
}

export const KLOW_QUERY_KEYS = {
  articles: (params: { search?: string; mine?: boolean } = {}) =>
    ["klow", "articles", params] as const,
  categories: () => ["klow", "categories"] as const,
  rsocTemplates: () => ["klow", "templates", "rsoc"] as const,
  googleTemplates: () => ["klow", "templates", "google"] as const,
  pixels: () => ["klow", "pixels"] as const,
} as const

export function useKlowArticles(
  options: { search?: string; enabled?: boolean; mine?: boolean } = {},
) {
  const search = options.search?.trim() || undefined
  const mine = options.mine ?? true
  return useQuery<KlowArticlesListResponse>({
    queryKey: KLOW_QUERY_KEYS.articles({ search, mine }),
    queryFn: () => {
      const params = new URLSearchParams({ page_size: "100", mine: String(mine) })
      if (search) params.set("search", search)
      return apiFetch<KlowArticlesListResponse>(`/api/klow/articles?${params}`)
    },
    enabled: options.enabled ?? true,
    retry: false,
  })
}

export function useSyncKlowArticles() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<KlowArticlesListResponse>(
        "/api/klow/articles?sync_remote=true&page_size=200&hits=200&content=false",
      ),
    onSuccess: (data) => {
      void qc.setQueryData(KLOW_QUERY_KEYS.articles({}), data)
      void qc.invalidateQueries({ queryKey: ["klow", "articles"] })
    },
  })
}

export function useKlowCategories(enabled = true) {
  return useQuery<{ success: boolean; data: KlowCategory[] }>({
    queryKey: KLOW_QUERY_KEYS.categories(),
    queryFn: () => apiFetch("/api/klow/categories"),
    enabled,
    retry: false,
  })
}

export function useKlowRsocTemplates(enabled = true) {
  return useQuery<{ success: boolean; data: RsocTemplate[] }>({
    queryKey: KLOW_QUERY_KEYS.rsocTemplates(),
    queryFn: () => apiFetch("/api/klow/templates/rsoc"),
    enabled,
    retry: false,
  })
}

export function useKlowGoogleTemplates(enabled = true) {
  return useQuery<{ success: boolean; data: GoogleTemplate[] }>({
    queryKey: KLOW_QUERY_KEYS.googleTemplates(),
    queryFn: () => apiFetch("/api/klow/templates/google"),
    enabled,
    retry: false,
  })
}

export function useKlowPixels(
  options: { enabled?: boolean; userId?: number | null; mine?: boolean } = {},
) {
  const { enabled = true, userId, mine = false } = options
  return useQuery<KlowPixelsResponse>({
    queryKey: [...KLOW_QUERY_KEYS.pixels(), mine ? "mine" : "all", userId ?? "anonymous"],
    queryFn: () =>
      apiFetch(mine ? "/api/klow/pixels?mine=true" : "/api/klow/pixels"),
    enabled: enabled && userId != null,
    retry: 1,
    staleTime: 0,
    refetchOnMount: "always",
  })
}

export function useKlowPixelDetails(pixelId: number | null) {
  return useQuery<KlowPixelDetailsResponse>({
    queryKey: ["klow", "pixels", pixelId],
    queryFn: () => apiFetch(`/api/klow/pixels/${pixelId}`),
    enabled: pixelId !== null,
    retry: false,
  })
}

export function useCreateKlowPixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<KlowPixelDetailsResponse>("/api/klow/pixels", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KLOW_QUERY_KEYS.pixels() })
    },
  })
}

export function useUpdateKlowPixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; body: Record<string, unknown> }) =>
      apiFetch<KlowPixelDetailsResponse>(`/api/klow/pixels/${data.id}`, {
        method: "PATCH",
        body: data.body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KLOW_QUERY_KEYS.pixels() })
    },
  })
}

export function useDeleteKlowPixel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pixelId: number) =>
      apiFetch<{ success: boolean; message: string }>(`/api/klow/pixels/${pixelId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KLOW_QUERY_KEYS.pixels() })
    },
  })
}

export function useKlowGenerateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<KlowGenerateResponse>("/api/klow/articles/generate", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["klow", "articles"] })
    },
  })
}

export function useKlowBulkGenerate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { requests: KlowBulkGenerateItem[] }) =>
      apiFetch<KlowGenerateResponse>("/api/klow/articles/generate/bulk", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["klow", "articles"] })
    },
  })
}

export function useKlowGenerationStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      request_id?: number
      bulk_id?: string
      job_id?: number
    }) =>
      apiFetch<KlowGenerationStatusResponse>(
        "/api/klow/articles/generate/status",
        { method: "POST", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["klow", "articles"] })
    },
  })
}

export function useKlowArticle(articleId: number | null, enabled = true) {
  return useQuery<{ success: boolean; data: KlowArticle }>({
    queryKey: ["klow", "article", articleId],
    queryFn: () => apiFetch(`/api/klow/articles/${articleId}`),
    enabled: enabled && articleId != null,
    retry: false,
  })
}

export function useCreateKlowOffer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      klow_article_id?: number
      vertical_slug?: string
      vertical_name?: string
      rsoc_template_id?: string
      google_template_id?: string
      ad_copy_text?: string
      suggested_keywords?: string
      traffic_source?: string
      pixel_id?: string
      user_tracking?: string
      user_tracking_2?: string
      generate?: Record<string, unknown>
    }) =>
      apiFetch<KlowOfferCreateResponse>("/api/klow/offers", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
      void qc.invalidateQueries({ queryKey: ["klow", "articles"] })
    },
  })
}

export interface KlowOfferUpdateResponse {
  success: boolean
  data: {
    id: number
    public_slug: string
    name: string
    network: string
    target_url: string | null
    status: string
    klow_article_id: number | null
    klow_rsoc_template_id: string | null
    klow_google_template_id: string | null
    klow_pixel_id: string | null
    klow_suggested_keywords: string | null
    klow_ad_copy_text: string | null
    klow_article_slug: string | null
    klow_article_url: string | null
    vertical_slug: string | null
    vertical_name: string | null
  }
}

export function useUpdateKlowOffer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      offerId: number
      body: {
        name?: string
        vertical_slug?: string | null
        vertical_name?: string | null
        rsoc_template_id?: string
        google_template_id?: string
        suggested_keywords?: string
        pixel_id?: string
        ad_copy_text?: string
      }
    }) =>
      apiFetch<KlowOfferUpdateResponse>(`/api/klow/offers/${data.offerId}`, {
        method: "PATCH",
        body: data.body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
    },
  })
}

export type { VerticalOption }
